import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Paperclip, CheckCircle2, Trash2, Receipt, AlertTriangle, Clock, Euro, Download } from "lucide-react";
import { ConfirmDialog } from "../shared/ConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n) || 0);
const PARTIDAS = ["Presupuesto propio", "CDTI", "Subvención UE", "Subvención autonómica/estatal", "Cliente / Facturación externa", "Otro"];

function EstadoBadge({ inv }) {
  if (inv.estado === "Pagada") return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid={`invoice-estado-${inv.id}`}>Pagada</Badge>;
  if (inv.vencida) return <Badge className="bg-red-100 text-red-700 border-red-200" data-testid={`invoice-estado-${inv.id}`}>Vencida · {inv.dias_retraso}d</Badge>;
  return <Badge className="bg-amber-100 text-amber-700 border-amber-200" data-testid={`invoice-estado-${inv.id}`}>Pendiente{inv.dias_hasta_vencimiento != null ? ` · vence en ${inv.dias_hasta_vencimiento}d` : ""}</Badge>;
}

/**
 * PG07InvoicesPanel — seguimiento de facturas (subida, pago, días) de proyectos PG-07.
 * Modo "embedded" (dentro de la ficha de un proyecto): projectId fijo, sin selector de proyecto.
 * Modo global (dashboard "Facturas" de PG-07): sin projectId, incluye selector + columna proyecto.
 */
export default function PG07InvoicesPanel({ authH, projectId, projectName, compact = false }) {
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ estado: "all", proveedor: "", partida_presupuestaria: "all", project_id: projectId || "all" });
  const [createOpen, setCreateOpen] = useState(false);

  const buildQuery = useCallback(() => {
    const p = new URLSearchParams();
    const pid = projectId || (filters.project_id !== "all" ? filters.project_id : "");
    if (pid) p.set("project_id", pid);
    if (filters.estado !== "all") p.set("estado", filters.estado);
    if (filters.proveedor) p.set("proveedor", filters.proveedor);
    if (filters.partida_presupuestaria !== "all") p.set("partida_presupuestaria", filters.partida_presupuestaria);
    return p.toString();
  }, [filters, projectId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildQuery();
      const [invR, sumR] = await Promise.all([
        fetch(`${API}/pg07/invoices?${qs}`, { headers: authH }),
        fetch(`${API}/pg07/invoices/summary${projectId ? `?project_id=${projectId}` : ""}`, { headers: authH }),
      ]);
      if (invR.ok) setInvoices(await invR.json());
      if (sumR.ok) setSummary(await sumR.json());
    } finally { setLoading(false); }
  }, [authH, buildQuery, projectId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (projectId) return;
    fetch(`${API}/pg07/projects`, { headers: authH }).then(r => r.ok ? r.json() : []).then(setProjects).catch(() => {});
  }, [authH, projectId]);

  const markPaid = async (inv) => {
    const r = await fetch(`${API}/pg07/invoices/${inv.id}/mark-paid`, { method: "POST", headers: { ...authH, "Content-Type": "application/json" }, body: JSON.stringify({}) });
    if (r.ok) { toast.success("Factura marcada como pagada"); load(); } else toast.error("Error al marcar como pagada");
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const deleteInvoice = (inv) => setDeleteTarget(inv);

  const confirmDeleteInvoice = async () => {
    if (!deleteTarget) return;
    const r = await fetch(`${API}/pg07/invoices/${deleteTarget.id}`, { method: "DELETE", headers: authH });
    if (r.ok) { toast.success("Factura eliminada"); load(); } else toast.error("Error al eliminar");
    setDeleteTarget(null);
  };

  const downloadAttachment = async (inv) => {
    try {
      const r = await fetch(`${API}/pg07/invoices/${inv.id}/attachment`, { headers: authH });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("No se pudo abrir el adjunto"); }
  };

  return (
    <div className="space-y-4" data-testid="pg07-invoices-panel">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card data-testid="invoices-kpi-pendiente"><CardContent className="p-3">
            <div className="text-[10px] uppercase text-muted-foreground font-bold flex items-center gap-1"><Clock className="w-3 h-3" />Pendiente</div>
            <div className="text-lg font-black text-amber-600">{eur(summary.total_pendiente)}</div>
            <div className="text-[10px] text-muted-foreground">{summary.count_pendientes} factura(s)</div>
          </CardContent></Card>
          <Card data-testid="invoices-kpi-vencido"><CardContent className="p-3">
            <div className="text-[10px] uppercase text-muted-foreground font-bold flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Vencido</div>
            <div className="text-lg font-black text-red-600">{eur(summary.total_vencido)}</div>
            <div className="text-[10px] text-muted-foreground">{summary.count_vencidas} vencida(s)</div>
          </CardContent></Card>
          <Card data-testid="invoices-kpi-pagado"><CardContent className="p-3">
            <div className="text-[10px] uppercase text-muted-foreground font-bold flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Pagado</div>
            <div className="text-lg font-black text-emerald-600">{eur(summary.total_pagado)}</div>
            <div className="text-[10px] text-muted-foreground">{summary.count_pagadas} factura(s)</div>
          </CardContent></Card>
          <Card data-testid="invoices-kpi-proximos"><CardContent className="p-3">
            <div className="text-[10px] uppercase text-muted-foreground font-bold flex items-center gap-1"><Euro className="w-3 h-3" />Próx. 7 días</div>
            <div className="text-lg font-black">{summary.proximos_vencimientos.length}</div>
            <div className="text-[10px] text-muted-foreground">por vencer</div>
          </CardContent></Card>
        </div>
      )}

      <div className="flex items-center flex-wrap gap-2">
        {!projectId && (
          <Select value={filters.project_id} onValueChange={v => setFilters({ ...filters, project_id: v })}>
            <SelectTrigger className="w-[220px] h-8 text-xs" data-testid="invoices-filter-project"><SelectValue placeholder="Proyecto" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Todos los proyectos</SelectItem>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Select value={filters.estado} onValueChange={v => setFilters({ ...filters, estado: v })}>
          <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="invoices-filter-estado"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="Pendiente">Pendiente</SelectItem><SelectItem value="Pagada">Pagada</SelectItem></SelectContent>
        </Select>
        <Select value={filters.partida_presupuestaria} onValueChange={v => setFilters({ ...filters, partida_presupuestaria: v })}>
          <SelectTrigger className="w-[190px] h-8 text-xs" data-testid="invoices-filter-partida"><SelectValue placeholder="Partida" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todas las partidas</SelectItem>{PARTIDAS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <Input placeholder="Buscar proveedor..." value={filters.proveedor} onChange={e => setFilters({ ...filters, proveedor: e.target.value })} className="w-[180px] h-8 text-xs" data-testid="invoices-filter-proveedor" />
        <Button size="sm" className="ml-auto h-8" onClick={() => setCreateOpen(true)} data-testid="pg07-invoice-create-btn"><Plus className="w-3.5 h-3.5 mr-1" />Nueva factura</Button>
      </div>

      {loading ? (
        <div className="flex justify-center p-8"><Loader2 className="w-5 h-5 animate-spin text-emerald-600" /></div>
      ) : invoices.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground" data-testid="invoices-empty-state">
          <Receipt className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm mb-3">Sin facturas registradas todavía</p>
          <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)} data-testid="invoices-empty-cta">
            <Plus className="w-3.5 h-3.5 mr-1" />Registrar la primera factura
          </Button>
        </CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="pg07-invoices-list">
          {invoices.map((inv, i) => (
            <Card key={inv.id} data-testid={`pg07-invoice-row-${i}`}>
              <CardContent className="p-3 flex items-center gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold">{inv.numero_factura || "(sin número)"}</span>
                    <EstadoBadge inv={inv} />
                    <Badge variant="outline" className="text-[9px]">{inv.partida_presupuestaria}</Badge>
                    {!projectId && inv.project_name && <span className="text-[10px] text-muted-foreground">{inv.project_name}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{inv.proveedor}{inv.concepto ? ` · ${inv.concepto}` : ""}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">Emisión {inv.fecha_emision} ({inv.dias_transcurridos}d) · Vencimiento {inv.fecha_vencimiento}{inv.fecha_pago ? ` · Pagada el ${inv.fecha_pago}` : ""}</div>
                </div>
                <div className="text-lg font-black flex-shrink-0">{eur(inv.importe)}</div>
                <div className="flex gap-1 flex-shrink-0">
                  {inv.has_attachment && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadAttachment(inv)} data-testid={`pg07-invoice-attachment-${inv.id}`}>
                      <Download className="w-3 h-3 mr-1" />Adjunto
                    </Button>
                  )}
                  {inv.estado !== "Pagada" && (
                    <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-700" onClick={() => markPaid(inv)} data-testid={`pg07-invoice-mark-paid-${inv.id}`}>
                      <CheckCircle2 className="w-3 h-3 mr-1" />Marcar pagada
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={() => deleteInvoice(inv)} data-testid={`pg07-invoice-delete-${inv.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <InvoiceCreateDialog
        open={createOpen} onClose={() => setCreateOpen(false)} authH={authH}
        projectId={projectId} projectName={projectName} projects={projects}
        onCreated={() => { setCreateOpen(false); load(); }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Eliminar factura"
        description={`¿Estás seguro de que quieres eliminar la factura "${deleteTarget?.numero_factura || "(sin número)"}"? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        onConfirm={confirmDeleteInvoice}
        testId="delete-invoice-confirm-dialog"
      />
    </div>
  );
}

function InvoiceCreateDialog({ open, onClose, authH, projectId, projectName, projects, onCreated }) {
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({
      project_id: projectId || "", numero_factura: "", proveedor: "", concepto: "",
      importe: "", fecha_emision: new Date().toISOString().slice(0, 10), fecha_vencimiento: "",
      partida_presupuestaria: "Presupuesto propio", notas: "",
    });
    setFile(null);
  }, [open, projectId]);

  const submit = async () => {
    if (!form.project_id || !form.proveedor || !form.importe || !form.fecha_emision || !form.fecha_vencimiento) {
      toast.error("Completa proyecto, proveedor, importe y fechas");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("file", file);
      const r = await fetch(`${API}/pg07/invoices`, { method: "POST", headers: { Authorization: authH.Authorization }, body: fd });
      if (!r.ok) throw new Error((await r.json()).detail || `HTTP ${r.status}`);
      toast.success("Factura registrada");
      onCreated();
    } catch (e) { toast.error(`Error: ${e.message}`); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="pg07-invoice-create-dialog">
        <DialogHeader><DialogTitle>Nueva factura</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto">
          {!projectId ? (
            <div><Label className="text-xs">Proyecto PG-07 *</Label>
              <Select value={form.project_id || ""} onValueChange={v => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="invoice-create-project"><SelectValue placeholder="Selecciona proyecto" /></SelectTrigger>
                <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">Proyecto: <strong>{projectName}</strong></div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Nº factura</Label><Input value={form.numero_factura || ""} onChange={e => setForm({ ...form, numero_factura: e.target.value })} data-testid="invoice-create-numero" /></div>
            <div><Label className="text-xs">Importe (€) *</Label><Input type="number" step={0.01} value={form.importe || ""} onChange={e => setForm({ ...form, importe: e.target.value })} data-testid="invoice-create-importe" /></div>
          </div>
          <div><Label className="text-xs">Proveedor / Emisor *</Label><Input value={form.proveedor || ""} onChange={e => setForm({ ...form, proveedor: e.target.value })} data-testid="invoice-create-proveedor" /></div>
          <div><Label className="text-xs">Concepto</Label><Input value={form.concepto || ""} onChange={e => setForm({ ...form, concepto: e.target.value })} data-testid="invoice-create-concepto" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Fecha emisión *</Label><Input type="date" value={form.fecha_emision || ""} onChange={e => setForm({ ...form, fecha_emision: e.target.value })} data-testid="invoice-create-emision" /></div>
            <div><Label className="text-xs">Fecha vencimiento *</Label><Input type="date" value={form.fecha_vencimiento || ""} onChange={e => setForm({ ...form, fecha_vencimiento: e.target.value })} data-testid="invoice-create-vencimiento" /></div>
          </div>
          <div><Label className="text-xs">Partida presupuestaria</Label>
            <Select value={form.partida_presupuestaria || "Presupuesto propio"} onValueChange={v => setForm({ ...form, partida_presupuestaria: v })}>
              <SelectTrigger data-testid="invoice-create-partida"><SelectValue /></SelectTrigger>
              <SelectContent>{PARTIDAS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Notas</Label><Textarea rows={2} value={form.notas || ""} onChange={e => setForm({ ...form, notas: e.target.value })} data-testid="invoice-create-notas" /></div>
          <div>
            <Label className="text-xs flex items-center gap-1"><Paperclip className="w-3 h-3" />Adjuntar PDF/imagen (opcional)</Label>
            <Input type="file" accept=".pdf,image/*" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="invoice-create-file" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="invoice-create-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}Registrar factura
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
